// Save queue for the Internal Audit worksheet (two auditors, patchy Wi-Fi — Oct 2026).
// Every change is put here FIRST, then saved. Nothing is lost when a save fails: the change stays queued (also in
// this device's storage, so it survives a page reload) and is retried until it is saved. Only the fields that were
// changed are sent — never the whole page — so two auditors can't overwrite each other's work.
//
//   put('rep', null, 'start_at', value)        a report setting   → saved through ia_report_patch (one call)
//   put('resp', itemId, 'notes', value)        a checklist answer → one upsert per item, changed columns only
//
// A checklist answer also carries the value the box showed BEFORE it was changed ("base"). The database saves a box
// only if nobody else changed it since (otherwise the newer saved value is kept and onConflict tells the auditor) —
// so a change that waited on an offline laptop can't overwrite the co-auditor's newer work.
//
// A newer change to the same field replaces the older one. An entry is removed only if it wasn't changed again
// while it was being saved. Anything that MIGHT work later is kept and retried — no connection, an expired login
// (after a laptop slept offline), a permission hiccup, and "the report is locked" (kept until the lead unlocks or the
// auditor chooses to discard). Only a deliberate refusal that can never succeed is dropped and reported through
// onRejected (e.g. "already submitted").
const keepAndRetry = e => {
  if (!e) return true
  const code = String(e.code || '')
  const msg = String(e.message || e)
  if (code === 'P0001') return /sign in|locked/i.test(msg)   // our own refusals: only these two can still succeed
  if (/^23/.test(code)) return false                         // data rule broken — retrying won't help
  return true                                                // network / login / permission: try again later
}

export function createSaveQueue({ storageKey, saveRep, saveResp, onChange, onRejected, onConflict }) {
  const items = new Map()   // key → { kind, itemId, field, value, seq, base?, hasBase? }
  let seq = 0
  let running = null
  let saves = 0          // successful saves so far — lets the page ignore a refresh that started before a save landed
  let lockedHeld = false // the last attempt was refused because the report is signed and locked
  const keyOf = (kind, itemId, field) => kind === 'rep' ? `rep:${field}` : `resp:${itemId}:${field}`
  const persist = () => {
    try {
      if (items.size) localStorage.setItem(storageKey, JSON.stringify([...items.values()]))
      else localStorage.removeItem(storageKey)
    } catch (e) { /* private window / storage full — the queue still works in memory */ }
  }
  const changed = () => { persist(); onChange && onChange(items.size) }
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || '[]')
    if (Array.isArray(saved)) saved.forEach(e => { if (e && e.kind && e.field) items.set(keyOf(e.kind, e.itemId, e.field), { ...e, seq: ++seq }) })
  } catch (e) { /* unreadable — start empty */ }

  // `base` (answers only): the value shown before this change. A later change to the same box keeps the FIRST base —
  // the value this device last saw saved — until it is saved.
  function put(kind, itemId, field, value, base) {
    const k = keyOf(kind, itemId, field)
    const prev = items.get(k)
    const hasBase = prev ? !!prev.hasBase : base !== undefined
    items.set(k, { kind, itemId: itemId || null, field, value, seq: ++seq, ...(hasBase ? { hasBase, base: prev ? prev.base : base } : {}) })
    changed()
  }
  const removeIfUnchanged = list => list.forEach(e => { const k = keyOf(e.kind, e.itemId, e.field); if (items.get(k)?.seq === e.seq) items.delete(k) })

  async function run() {
    const all = [...items.values()]
    if (!all.length) return true
    let kept = false
    lockedHeld = false
    const fail = (list, err) => {
      if (keepAndRetry(err)) { kept = true; if (/locked/i.test(String(err?.message || ''))) lockedHeld = true; return }
      removeIfUnchanged(list)                      // refused for good — retrying would never work
      onRejected && onRejected(err, list)
    }
    const ok = list => { removeIfUnchanged(list); saves++ }
    const reps = all.filter(e => e.kind === 'rep')
    if (reps.length) {
      try { await saveRep(Object.fromEntries(reps.map(e => [e.field, e.value]))); ok(reps) }
      catch (err) { fail(reps, err) }
    }
    const byItem = {}
    all.filter(e => e.kind === 'resp').forEach(e => { (byItem[e.itemId] = byItem[e.itemId] || []).push(e) })
    for (const [itemId, list] of Object.entries(byItem)) {
      try {
        const bases = Object.fromEntries(list.filter(e => e.hasBase).map(e => [e.field, e.base ?? null]))
        const res = await saveResp(itemId, Object.fromEntries(list.map(e => [e.field, e.value])), bases)
        const conflicts = (res && res.conflicts) || {}
        ok(list)
        // A box changed again while saving stays queued. If OUR value was saved, that is now its base. If it clashed,
        // it keeps its old base, so it clashes again — the co-auditor's newer text is never overwritten unseen.
        list.forEach(e => {
          const cur = items.get(keyOf(e.kind, e.itemId, e.field))
          if (cur && cur.hasBase && !(e.field in conflicts)) cur.base = e.value
        })
        list.filter(e => e.field in conflicts).forEach(e => onConflict && onConflict(itemId, e.field, e.value, conflicts[e.field]))
      }
      catch (err) { fail(list, err) }
    }
    changed()
    return !kept && items.size === 0
  }

  // Save everything queued now. Calls made while a save is running wait for it, then save anything newer.
  async function flush() {
    while (running) { try { await running } catch (e) { /* handled inside run */ } }
    running = run()
    try { return await running } finally { running = null }
  }

  return {
    put, flush,
    size: () => items.size,
    saves: () => saves,
    busy: () => !!running,
    lockedHeld: () => lockedHeld && items.size > 0,
    // "Discard unsaved changes on this device" — the auditor's own escape hatch.
    clear: () => { items.clear(); lockedHeld = false; changed() },
    // Queued (not yet saved) values, to show on screen over what the server has.
    overlayRep: rep => { const r = { ...rep }; items.forEach(e => { if (e.kind === 'rep') r[e.field] = e.value }); return r },
    overlayResp: resp => {
      const out = { ...resp }
      items.forEach(e => { if (e.kind === 'resp') out[e.itemId] = { ...(out[e.itemId] || { item_id: e.itemId }), [e.field]: e.value } })
      return out
    },
    has: (kind, itemId, field) => items.has(keyOf(kind, itemId, field)),
    hasItem: itemId => [...items.values()].some(e => e.kind === 'resp' && e.itemId === itemId),
  }
}
